import React from 'react'
import { useQuery, useMutation } from 'convex/react'
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid'
import { api } from '../../convex_generated/api'
import { useAuth } from '../../hooks/useAuth'

interface FollowContentButtonProps {
  targetType: 'book' | 'branch'
  targetId: string
  compact?: boolean
}

const FollowContentButton: React.FC<FollowContentButtonProps> = ({
  targetType,
  targetId,
  compact = false,
}) => {
  const { user, isAuthenticated } = useAuth()

  const followStatus = useQuery(
    api.users.checkContentFollowStatus,
    isAuthenticated && user ? { targetType, targetId } : 'skip',
  )

  const followerCount = useQuery(api.users.getContentFollowerCount, {
    targetType,
    targetId,
  })

  const followContent = useMutation(api.users.followContent)
  const unfollowContent = useMutation(api.users.unfollowContent)

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!isAuthenticated || !user) return
    if (followStatus?.isFollowing) {
      await unfollowContent({ targetType, targetId })
    } else {
      await followContent({ targetType, targetId })
    }
  }

  if (!isAuthenticated) return null

  const isFollowing = followStatus?.isFollowing ?? false
  const count = followerCount ?? 0

  if (compact) {
    return (
      <button
        onClick={handleToggle}
        className={`p-1.5 rounded-lg transition-colors ${
          isFollowing
            ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
            : 'text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        title={isFollowing ? `Unfollow this ${targetType}` : `Follow this ${targetType}`}
      >
        {isFollowing ? (
          <HeartSolid className="w-4 h-4" />
        ) : (
          <HeartOutline className="w-4 h-4" />
        )}
      </button>
    )
  }

  return (
    <button
      onClick={handleToggle}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        isFollowing
          ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}
    >
      {isFollowing ? (
        <HeartSolid className="w-3.5 h-3.5" />
      ) : (
        <HeartOutline className="w-3.5 h-3.5" />
      )}
      <span>{isFollowing ? 'Following' : 'Follow'}</span>
      {count > 0 && (
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          · {count}
        </span>
      )}
    </button>
  )
}

export default FollowContentButton
